import { apiGet, apiPost, apiPatch, apiDelete } from './apiClient';

export interface BrandCelebrityReference {
    id: number;
    name: string;
    startsAt: string | null;
    expiresAt: string;
    isActive: boolean;
    notes: string | null;
    status: 'active' | 'scheduled' | 'expired' | 'inactive' | 'expiring_soon';
    expiresInDays: number | null;
}

export interface BrandActivityReference {
    id: number;
    name: string;
    opinionLeaderName: string | null;
    startsAt: string | null;
    expiresAt: string | null;
    isEvergreen: boolean;
    isActive: boolean;
    notes: string | null;
    status: 'active' | 'scheduled' | 'expired' | 'inactive' | 'expiring_soon' | 'evergreen';
    expiresInDays: number | null;
}

export interface BrandReferenceLibrary {
    celebrities: {
        all: BrandCelebrityReference[];
        active: BrandCelebrityReference[];
        expired: BrandCelebrityReference[];
        expiringSoon: BrandCelebrityReference[];
        scheduled: BrandCelebrityReference[];
        inactive: BrandCelebrityReference[];
    };
    activities: {
        all: BrandActivityReference[];
        active: BrandActivityReference[];
        evergreen: BrandActivityReference[];
        expired: BrandActivityReference[];
        expiringSoon: BrandActivityReference[];
        scheduled: BrandActivityReference[];
        inactive: BrandActivityReference[];
    };
    summary: {
        expiredCelebrities: number;
        expiringCelebrities: number;
        expiredActivities: number;
        expiringActivities: number;
        activeCelebrities: number;
        activeActivities: number;
        evergreenActivities: number;
    };
}

interface ReferenceLibraryResponse {
    success: boolean;
    data?: BrandReferenceLibrary;
    error?: string;
}

export interface ReferenceMutationPayload {
    name?: string;
    opinionLeaderName?: string | null;
    startsAt?: string | null;
    expiresAt?: string | null;
    isActive?: boolean;
    isEvergreen?: boolean;
    notes?: string | null;
}

export function toLocalDateTimeInput(isoString: string | null): string {
    if (!isoString) return '';
    const date = new Date(isoString);
    const tzOffsetMs = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - tzOffsetMs).toISOString().slice(0, 16);
}

export function toIsoFromLocalDateTimeInput(value: string): string | null {
    if (!value.trim()) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toISOString();
}

export async function fetchReferenceLibrary(): Promise<BrandReferenceLibrary> {
    const res = await apiGet('/api/settings/reference-library');
    const data = await res.json() as ReferenceLibraryResponse;
    if (!data.success || !data.data) {
        throw new Error(data.error || '無法載入品牌資料庫');
    }
    return data.data;
}

export async function mutateReference(
    endpoint: string,
    method: 'POST' | 'PATCH' | 'DELETE',
    payload?: ReferenceMutationPayload
): Promise<void> {
    let res: Response;
    if (method === 'POST') {
        res = await apiPost(endpoint, payload);
    } else if (method === 'PATCH') {
        res = await apiPatch(endpoint, payload);
    } else {
        res = await apiDelete(endpoint);
    }
    
    const data = await res.json() as { success: boolean; error?: string };
    if (!res.ok || !data.success) {
        throw new Error(data.error || '品牌資料庫操作失敗');
    }
}
